import React, { Fragment, useMemo, useState } from 'react';
import {
  Box,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { DictionaryWord } from 'app/types';
import { ResolvedTableSettings, TableColumnKey } from 'components/tableSettings';
import AudioButton from 'components/dictionaryView/AudioButton';
import { Checkbox, POSTag, SELECT_COL_WIDTH } from './primitives';
import {
  WordStatus,
  fmtAddedDate,
  hexToRgba,
  statusColor,
  statusOf,
} from './meta';
import StatusCell from './StatusCell';
import GroupHeader from './GroupHeader';
import { UseVocabularyWordsViewState } from './useVocabularyWordsView';

interface ManagerTableProps {
  view: UseVocabularyWordsViewState;
  tableSettings: ResolvedTableSettings;
  /** One-shot status setter — replaces the two boolean togglers. */
  onSetStatus: (id: number, status: WordStatus) => void;
  onDeleteOne: (id: number) => void;
}

interface ColMeta {
  key: TableColumnKey;
  label: string;
  width: string;
  min: number;
}

const COL_TEMPLATE: Record<string, ColMeta> = {
  number: { key: 'number', label: '№', width: '46px', min: 46 },
  word: { key: 'word', label: 'Слово', width: '180px', min: 180 },
  transcription: {
    key: 'transcription',
    label: 'Транскрипція',
    width: '128px',
    min: 128,
  },
  translation: {
    key: 'translation',
    label: 'Переклад',
    width: '170px',
    min: 170,
  },
  partOfSpeech: {
    key: 'partOfSpeech',
    label: 'Частина мови',
    width: '128px',
    min: 128,
  },
  definition: {
    key: 'definition',
    label: 'Визначення',
    width: 'minmax(220px, 1fr)',
    min: 220,
  },
  /**
   * Single "Статус" column replaces the legacy `isLearned` / `isActiveSpeech`
   * pair — on the BE side this will eventually map to one enum value, so the
   * FE collapses it now to keep the table honest.
   */
  status: {
    key: 'status',
    label: 'Статус',
    width: '184px',
    min: 184,
  },
  addedAt: { key: 'addedAt', label: 'Додано', width: '140px', min: 140 },
};

const visibleColumnsFrom = (settings: ResolvedTableSettings): ColMeta[] =>
  settings.columns
    .filter((c) => c.visible && COL_TEMPLATE[c.key])
    .map((c) => COL_TEMPLATE[c.key]);

const tableMinWidth = (cols: ColMeta[]) =>
  SELECT_COL_WIDTH + cols.reduce((s, c) => s + c.min, 0);

const rowTint = (
  w: DictionaryWord,
  selected: boolean,
  highlight: boolean,
  hlLearned: boolean,
  hlActive: boolean,
  mode: 'light' | 'dark',
): string | undefined => {
  if (selected) {
    return mode === 'light'
      ? 'rgba(59,44,147,0.07)'
      : 'rgba(155,124,255,0.14)';
  }
  if (highlight) {
    return mode === 'light' ? 'rgba(39,204,188,0.14)' : 'rgba(63,224,206,0.18)';
  }
  const s = statusOf(w);
  if (s === 'ACTIVE' && hlActive) {
    return hexToRgba(statusColor('ACTIVE', mode), 0.08);
  }
  if (s === 'LEARNED' && hlLearned) {
    return hexToRgba(statusColor('LEARNED', mode), 0.07);
  }
  return undefined;
};

const HeaderRow: React.FC<{
  view: UseVocabularyWordsViewState;
  cols: ColMeta[];
  template: string;
}> = ({ view, cols, template }) => (
  <Box
    sx={{
      alignItems: 'center',
      bgcolor: 'primary.tableHeadBg',
      borderBottom: '1px solid',
      borderColor: 'primary.tableBorder',
      display: 'grid',
      gap: 0,
      gridTemplateColumns: template,
      height: 46,
      px: 1,
    }}
  >
    <Stack direction={'row'} justifyContent={'center'}>
      <Checkbox checked={view.allSelected} onClick={() => view.toggleAll()} />
    </Stack>
    {cols.map((c) => (
      <Typography
        key={c.key}
        sx={{
          color: 'text.secondary',
          fontSize: 12,
          fontWeight: 800,
          letterSpacing: '0.04em',
          px: 1.5,
          textTransform: 'uppercase',
        }}
      >
        {c.label}
      </Typography>
    ))}
  </Box>
);

const EmptyRow: React.FC = () => (
  <Box sx={{ color: 'text.secondary', px: 3, py: 6, textAlign: 'center' }}>
    <SearchOffIcon sx={{ color: 'primary.tableBorder', fontSize: 34 }} />
    <Typography sx={{ fontSize: 15, fontWeight: 600, mt: 1 }}>
      {'Нічого не знайдено'}
    </Typography>
    <Typography sx={{ fontSize: 13.5, mt: 0.25 }}>
      {'Спробуй змінити фільтри або пошук'}
    </Typography>
  </Box>
);

const RowEditField: React.FC<{
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}> = ({ value, onChange, placeholder }) => (
  <TextField
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    size={'small'}
    onClick={(e) => e.stopPropagation()}
    autoComplete={'off'}
    sx={{
      width: '100%',
      '& .MuiInputBase-root': {
        bgcolor: 'background.default',
        fontSize: 14,
        height: 34,
      },
      '& fieldset': { borderColor: 'primary.main' },
    }}
  />
);

const TableRow: React.FC<{
  word: DictionaryWord;
  index: number;
  view: UseVocabularyWordsViewState;
  tableSettings: ResolvedTableSettings;
  cols: ColMeta[];
  template: string;
  onSetStatus: (id: number, status: WordStatus) => void;
  onDeleteOne: (id: number) => void;
}> = ({
  word,
  index,
  view,
  tableSettings,
  cols,
  template,
  onSetStatus,
}) => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const [hover, setHover] = useState(false);
  const selected = !!view.selected[word.id];
  const editing = view.isEditing(word.id);
  const draft = editing ? view.draftFor(word.id) : null;
  const highlighted = view.highlightId === word.id;

  const tint = rowTint(
    word,
    selected,
    highlighted,
    tableSettings.highlightLearned,
    tableSettings.highlightActiveSpeech,
    mode,
  );

  const renderCell = (col: ColMeta) => {
    switch (col.key) {
      case 'number':
        return (
          <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>
            {index + 1}
          </Typography>
        );
      case 'word':
        return (
          <Stack
            direction={'row'}
            alignItems={'center'}
            spacing={1.125}
            sx={{ minWidth: 0 }}
          >
            <AudioButton word={word.word ?? ''} size={32} />
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 700,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {word.partOfSpeech === 'VERB' ? `to ${word.word}` : word.word}
            </Typography>
          </Stack>
        );
      case 'transcription':
        return (
          <Typography
            sx={{
              color: 'text.secondary',
              fontFamily: 'ui-monospace, monospace',
              fontSize: 14,
            }}
          >
            {word.transcription}
          </Typography>
        );
      case 'translation':
        return editing ? (
          <RowEditField
            value={draft!.translation}
            onChange={(v) => view.setDraft(word.id, { translation: v })}
            placeholder={'переклад'}
          />
        ) : (
          <Typography
            sx={{
              fontSize: 14.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {word.translation}
          </Typography>
        );
      case 'partOfSpeech':
        return <POSTag pos={word.partOfSpeech} />;
      case 'definition':
        return editing ? (
          <RowEditField
            value={draft!.definition}
            onChange={(v) => view.setDraft(word.id, { definition: v })}
            placeholder={'визначення'}
          />
        ) : (
          <Tooltip
            title={word.definition ?? ''}
            placement={'top'}
            arrow
            enterDelay={400}
            disableHoverListener={!word.definition}
          >
            <Typography
              sx={{
                color: 'text.secondary',
                display: '-webkit-box',
                fontSize: 14,
                lineHeight: 1.4,
                overflow: 'hidden',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
              }}
            >
              {word.definition}
            </Typography>
          </Tooltip>
        );
      case 'status':
        return <StatusCell word={word} onSetStatus={onSetStatus} />;
      case 'addedAt':
        return (
          <Typography sx={{ color: 'text.secondary', fontSize: 13.5 }}>
            {fmtAddedDate(word.addedAt)}
          </Typography>
        );
      default:
        return null;
    }
  };

  return (
    <Box
      onClick={() => (editing ? undefined : view.toggle(word.id))}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      sx={{
        alignItems: 'center',
        bgcolor: tint ?? 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'primary.tableBorder',
        boxShadow:
          hover && !selected && !editing
            ? (t) =>
                `inset 0 0 0 1.5px ${t.palette.primary.main}`
            : 'none',
        cursor: editing ? 'default' : 'pointer',
        display: 'grid',
        gridTemplateColumns: template,
        minHeight: 58,
        position: 'relative',
        px: 1,
        transition: 'background-color .12s ease',
      }}
    >
      <Stack direction={'row'} justifyContent={'center'}>
        <Checkbox
          checked={selected}
          onClick={() => view.toggle(word.id)}
        />
      </Stack>
      {cols.map((c) => (
        <Box
          key={c.key}
          sx={{ minWidth: 0, px: 1.5, py: 1 }}
        >
          {renderCell(c)}
        </Box>
      ))}

      {/* Inline edit save/cancel */}
      {editing && (
        <Stack
          direction={'row'}
          spacing={0.5}
          sx={{
            position: 'absolute',
            right: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1,
          }}
        >
          <IconButton
            size={'small'}
            onClick={(e) => {
              e.stopPropagation();
              view.saveEdit(word.id);
            }}
            title={'Зберегти'}
            sx={{
              bgcolor: 'primary.main',
              color: 'common.white',
              height: 32,
              width: 32,
              '&:hover': { bgcolor: 'primary.main', opacity: 0.92 },
            }}
          >
            <CheckIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton
            size={'small'}
            onClick={(e) => {
              e.stopPropagation();
              view.cancelEdit(word.id);
            }}
            title={'Скасувати'}
            sx={{
              border: '1px solid',
              borderColor: 'primary.tableBorder',
              color: 'text.secondary',
              height: 32,
              width: 32,
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>
      )}
    </Box>
  );
};

const ManagerTable: React.FC<ManagerTableProps> = ({
  view,
  tableSettings,
  onSetStatus,
  onDeleteOne,
}) => {
  const cols = useMemo(
    () => visibleColumnsFrom(tableSettings),
    [tableSettings],
  );
  const template = useMemo(
    () => [`${SELECT_COL_WIDTH}px`, ...cols.map((c) => c.width)].join(' '),
    [cols],
  );
  const minWidth = useMemo(() => tableMinWidth(cols), [cols]);

  let running = 0;
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'primary.tableBorder',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ bgcolor: 'background.paper', minWidth }}>
          <HeaderRow view={view} cols={cols} template={template} />
          {view.filtered.length === 0 ? (
            <EmptyRow />
          ) : (
            view.grouped.map((g) => (
              <Fragment key={g.key}>
                {g.label !== null && (
                  <GroupHeader group={g} view={view} />
                )}
                {g.words.map((w) => {
                  const idx = running++;
                  return (
                    <TableRow
                      key={w.id}
                      word={w}
                      index={idx}
                      view={view}
                      tableSettings={tableSettings}
                      cols={cols}
                      template={template}
                      onSetStatus={onSetStatus}
                      onDeleteOne={onDeleteOne}
                    />
                  );
                })}
              </Fragment>
            ))
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ManagerTable;
